import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { EditIssueImpactDto, FetchError, GetIssuesDto, IssueDto, UserDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { useSignalR } from "../../../hooks";
import { optimisticallyUpdateQueryData } from "../utilities";
import { isWhitespace } from "src/components";

type Variables = {
  issueGuid: string;
  impact: string;
  teamSlug?: string;
  createdByUserId: string;
  modifiedByUser: UserDto;
};

const useEditIssueImpactRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueImpactRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueImpactDto = {
      impact: variables.impact,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/impact`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueImpactRequest };
};

const useEditIssueImpact = () => {
  const { editIssueImpactRequest } = useEditIssueImpactRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueImpactRequest,
    onMutate: async (variables) => {
      let cachedTeamIssuesData: GetIssuesDto | undefined;

      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) =>
              issue.guid === variables.issueGuid
                ? {
                    ...issue,
                    hasImpact: !isWhitespace(variables.impact),
                  }
                : issue,
            ),
          }),
        );
      }

      const cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.map((issue) =>
            issue.guid === variables.issueGuid
              ? {
                  ...issue,
                  hasImpact: !isWhitespace(variables.impact),
                }
              : issue,
          ),
        }),
      );

      const cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          impact: {
            value: variables.impact,
            modifiedByUser: variables.modifiedByUser,
            modifiedDateTimeUtc: new Date().toISOString(),
          },
        }),
      );

      return { cachedTeamIssuesData, cachedUserIssuesData, cachedIssueData };
    },
    onError: (_error, variables, context) => {
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData,
        );
      }

      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        context?.cachedUserIssuesData,
      );
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }
      signalR.sendRefetchUserIssues(variables.createdByUserId);
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.teamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      }
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUserId));
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditIssueImpact };
